import { useState, useCallback, useRef, useMemo } from 'react';
import { makeStyles, tokens, mergeClasses, MessageBar, MessageBarBody, MessageBarTitle } from '@coreai/fluentui-react';
import { ShieldLockRegular } from '@fluentui/react-icons';
import { WorkflowDesigner } from './WorkflowDesigner';
import { CodeEditor } from './CodeEditor';
import { CodePanelHeader, CodeFileToolbar, CODE_PANEL_DESCRIPTION_ID, type CodePanelState } from './CodeEditor/CodePanelHeader';
import { ExecutionModeBanner } from './ExecutionModeBanner';
import { FeatureErrorBoundary } from '@/components/ErrorBoundary';
import { useEditorRunStore } from '@/stores/editorRunStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useShallow } from 'zustand/react/shallow';
import { useDraftModeStore, selectIsReadOnly } from '@/stores/draftModeStore';
import { serializeWorkflow } from '@/services/workflowSerializer';
import type { WorkflowDefinition } from '@/types/workflowDefinition';

type ActiveFile = 'workflow.json' | 'connections.json';

type EditorRunStoreState = ReturnType<typeof useEditorRunStore.getState>;

const selectDesignerRunState = (state: EditorRunStoreState) => ({
  isExecutionMode: state.isExecutionMode,
  runStateVersion: state.runStateVersion,
  getNodeExecutions: state.getNodeExecutions,
  agentIterations: state.agentIterations,
  setAgentIteration: state.setAgentIteration,
  getAgentIterationsData: state.getAgentIterationsData,
});

type DesignerLayoutProps = {
  codeState: CodePanelState;
  onCodeStateChange: (state: CodePanelState) => void;
  activeFile: ActiveFile;
  onFileChange: (file: ActiveFile) => void;
  hasConnections: boolean;
  parseError?: string | null;
  /** Callback when user requests to generate a workflow from empty canvas */
  onGenerateWorkflow?: ((prompt: string) => void) | undefined;
  /** Whether the chat sidebar is open */
  isChatSidebarOpen?: boolean;
  /** Toggle code panel visibility */
  onToggleCode?: () => void;
  /** Toggle chat sidebar */
  onToggleChat?: () => void;

  /** Open run workflow dialog */
  onRun?: () => void;
  /** Whether workflow has unsaved changes */
  isDirty?: boolean;
  /** Whether save is in progress */
  isSaving?: boolean;
  /** Trigger save */
  onSave?: () => void;
  /** Whether validation is in progress (shown during save) */
  isValidating?: boolean;
  /** Whether this is a conversational (A2A) workflow */
  isConversational?: boolean;
  /** Start a new chat (for conversational workflows) */
  onStartChat?: () => void;
  /** Current workflow for dirty node detection in execution mode */
  workflow?: WorkflowDefinition | null;
  /** Called when user exits debug mode */
  onExitDebugMode?: () => void;
  /** Whether draft mode is enabled */
  draftModeEnabled?: boolean;
  /** Workflow name (required for draft mode autosave) */
  workflowName?: string;
  /**
   * Stable identity for the current workflow within the full route hierarchy
   * (tenant/location/project/app/workflow). Used as the key on `<WorkflowDesigner>`
   * so its component-local state remounts whenever any of these change, not just
   * when the workflow name changes (two workflows in different apps can share names).
   */
  routeIdentityKey?: string;
  /** Callback to trigger validation */
  onValidate?: () => void;
  /** Number of validation errors (0 = valid, undefined = not validated) */
  validationErrorCount?: number;
  /** When true, hides the dock toolbar and shows a read-only indicator */
  isPermissionReadOnly?: boolean;
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  canvasPanel: {
    flex: 1,
    minWidth: 0,
  },
  codePanel: {
    display: 'flex',
    flexDirection: 'column',
  },
  codePanelMaximized: {
    flex: 1,
  },
  panelContent: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  codeGroup: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    marginInline: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM,
    marginTop: tokens.spacingVerticalS,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  codeGroupEditor: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  divider: {
    width: '4px',
    backgroundColor: tokens.colorNeutralStroke1,
    cursor: 'col-resize',
    flexShrink: 0,
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationFaster,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    ':hover': {
      backgroundColor: tokens.colorBrandStroke1,
    },
  },
  dividerDragging: {
    backgroundColor: tokens.colorBrandStroke1,
  },
  readOnlyBanner: {
    flexShrink: 0,
  },
  readOnlyIcon: {
    marginRight: tokens.spacingHorizontalSNudge,
    verticalAlign: 'text-bottom',
  },
});

const DEFAULT_CODE_WIDTH = 500;
const MIN_CANVAS_WIDTH = 300;
const MIN_CODE_WIDTH = 300;

export const DesignerLayout = ({
  codeState,
  onCodeStateChange,
  activeFile,
  onFileChange,
  hasConnections,
  parseError,
  onGenerateWorkflow,
  isChatSidebarOpen,
  onToggleCode,
  onToggleChat,
  onRun,
  isDirty,
  isSaving,
  onSave,
  isValidating,
  isConversational,
  onStartChat,
  workflow,
  onExitDebugMode,
  draftModeEnabled = false,
  workflowName,
  routeIdentityKey,
  onValidate,
  validationErrorCount,
  isPermissionReadOnly = false,
}: DesignerLayoutProps) => {
  const styles = useStyles();
  const [codeWidth, setCodeWidth] = useState(DEFAULT_CODE_WIDTH);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasPanelRef = useRef<HTMLDivElement>(null);
  const containerWidthRef = useRef(0);

  // Get execution mode state and handlers
  const {
    isExecutionMode,
    runStateVersion,
    getNodeExecutions,
    agentIterations: debugAgentIterations,
    setAgentIteration,
    getAgentIterationsData,
  } = useEditorRunStore(useShallow(selectDesignerRunState));

  // Get node executions for debug mode rendering
  const debugNodeExecutionSnapshot = useMemo(
    () => ({
      version: runStateVersion,
      data: isExecutionMode ? getNodeExecutions() : undefined,
    }),
    [isExecutionMode, getNodeExecutions, runStateVersion],
  );
  const debugNodeExecutions = debugNodeExecutionSnapshot.data;

  // Get agent iterations data for debug mode (tool invocation status)
  const debugAgentIterationsSnapshot = useMemo(
    () => ({
      version: runStateVersion,
      data: isExecutionMode ? getAgentIterationsData() : undefined,
    }),
    [isExecutionMode, getAgentIterationsData, runStateVersion],
  );
  const debugAgentIterationsData = debugAgentIterationsSnapshot.data;

  // Handler for iteration changes in debug mode
  const handleDebugIterationChange = useCallback((agentName: string, iteration: number) => {
    setAgentIteration(agentName, iteration);
  }, [setAgentIteration]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    if (containerRef.current) {
      containerWidthRef.current = containerRef.current.getBoundingClientRect().width;
    }

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newCodeWidth = containerRect.right - moveEvent.clientX;

      // Clamp to min/max values
      const maxCodeWidth = containerRect.width - MIN_CANVAS_WIDTH;
      const clampedWidth = Math.max(MIN_CODE_WIDTH, Math.min(maxCodeWidth, newCodeWidth));

      setCodeWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  // Code panel handlers — Close fully hides, Expand toggles maximized/normal.
  const handleCodeClose = useCallback(() => onCodeStateChange('hidden'), [onCodeStateChange]);
  const handleCodeToggleMaximize = useCallback(
    () => onCodeStateChange(codeState === 'maximized' ? 'normal' : 'maximized'),
    [codeState, onCodeStateChange],
  );

  // File dropdown options
  const fileOptions = useMemo(
    () => [
      { value: 'workflow.json', label: 'workflow.json' },
      { value: 'connections.json', label: 'connections.json', disabled: !hasConnections },
    ],
    [hasConnections],
  );

  // Determine visibility and sizes based on states
  const isCodeVisible = codeState !== 'hidden';
  const isCodeMaximized = codeState === 'maximized';
  const showCanvas = !isCodeMaximized;
  const showDivider = isCodeVisible && !isCodeMaximized;

  // Pull the current file content for Copy/Download. Mirrors CodeEditor's logic
  // so what the user copies matches what they see in the editor. `useShallow`
  // ensures we only re-render when one of these three fields actually changes,
  // not on every unrelated workflowStore mutation (agent iterations, selection,
  // node executions, etc.).
  const { workflow: storeWorkflow, workflowJson, connectionsJson } = useWorkflowStore(
    useShallow((s) => ({
      workflow: s.workflow,
      workflowJson: s.workflowJson,
      connectionsJson: s.connectionsJson,
    })),
  );
  const isReadOnly = useDraftModeStore(selectIsReadOnly);

  const fileContent = useMemo(() => {
    if (activeFile !== 'workflow.json') return connectionsJson ?? '';
    if (!storeWorkflow) return workflowJson ?? '';
    try {
      return serializeWorkflow(storeWorkflow, { pretty: true });
    } catch {
      return workflowJson ?? '';
    }
  }, [activeFile, storeWorkflow, workflowJson, connectionsJson]);

  // Resolve the two read-only concepts that can gate the code panel.
  // Permission-read-only (RBAC — user lacks edit access on the app) takes
  // precedence over draft-mode-read-only (user is viewing the published
  // version and can flip back to draft), because switching modes doesn't
  // help someone without permission. `isEditorReadOnly` gates CodeMirror
  // itself so a permission-blocked user cannot type edits that would fail
  // server-side. `readOnlyBadge.tooltip` drives the Fluent tooltip on the
  // "Read-only" chip in the toolbar.
  const isEditorReadOnly = isPermissionReadOnly || isReadOnly;
  const readOnlyBadge = isPermissionReadOnly
    ? { tooltip: 'Contact your app administrator to request access' }
    : isReadOnly
      ? { tooltip: 'Switch to draft mode to edit' }
      : null;

  // Calculate code panel style
  const getCodePanelStyle = (): React.CSSProperties => {
    if (isCodeMaximized) return { flex: 1 };
    return { width: codeWidth };
  };

  return (
    <div ref={containerRef} className={styles.container}>
      {/* Canvas Panel — no header, fills its area edge-to-edge */}
      {showCanvas && (
        <div className={mergeClasses(styles.panel, styles.canvasPanel)}>
          <div ref={canvasPanelRef} className={styles.panelContent}>
            {isPermissionReadOnly && (
              <MessageBar intent="warning" layout="singleline" className={styles.readOnlyBanner}>
                <MessageBarBody>
                  <ShieldLockRegular className={styles.readOnlyIcon} aria-hidden="true" />
                  <MessageBarTitle>Read-only</MessageBarTitle>
                  You don't have the correct permissions to edit this workflow.
                </MessageBarBody>
              </MessageBar>
            )}
            <ExecutionModeBanner workflow={workflow ?? null} onExit={onExitDebugMode} />
            <FeatureErrorBoundary componentName="WorkflowCanvas">
              <WorkflowDesigner
                key={routeIdentityKey ?? workflowName}
                isChatSidebarOpen={isChatSidebarOpen}
                // Command bar props
                isCodeVisible={isCodeVisible}
                isDirty={isDirty ?? false}
                isSaving={isSaving ?? false}
                isConversational={isConversational ?? false}
                isValidating={isValidating ?? false}
                commandBarContainerRef={canvasPanelRef}
                commandBarTopOffset={isExecutionMode ? 40 : isPermissionReadOnly ? 36 : 0}
                // Draft mode props
                draftModeEnabled={draftModeEnabled}
                workflowName={workflowName}
                {...(onGenerateWorkflow && !isPermissionReadOnly && { onGenerateWorkflow })}
                // Allow toggling the code panel even in permission read-only
                // mode. Close (×) now fully hides the panel and the panel
                // defaults to `hidden`, so stripping onToggleCode here would
                // leave read-only users unable to open the code view at all.
                // Editing is already blocked via CodeEditor readOnly={...} and
                // the toolbar shows a "Read-only" badge, so exposing the
                // toggle is safe.
                {...(onToggleCode && { onToggleCode })}
                {...(onToggleChat && !isPermissionReadOnly && { onToggleChat })}

                {...(onRun && !isPermissionReadOnly && { onRun })}
                {...(onSave && !isPermissionReadOnly && { onSave })}
                {...(onStartChat && !isPermissionReadOnly && { onStartChat })}
                {...(onValidate && !isPermissionReadOnly && { onValidate })}
                {...(validationErrorCount !== undefined && { validationErrorCount })}

                {...(debugNodeExecutions && debugNodeExecutions.length > 0 ? {
                  nodeExecutions: debugNodeExecutions,
                  agentIterations: debugAgentIterations,
                  agentIterationsData: debugAgentIterationsData,
                  onIterationChange: handleDebugIterationChange,
                } : {})}
              />
            </FeatureErrorBoundary>
          </div>
        </div>
      )}

      {/* Divider */}
      {showDivider && (
        <div
          className={mergeClasses(styles.divider, isDragging && styles.dividerDragging)}
          onMouseDown={handleMouseDown}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panels"
          aria-valuenow={containerWidthRef.current > 0 ? Math.round((codeWidth / containerWidthRef.current) * 100) : 50}
          aria-valuemin={0}
          aria-valuemax={100}
          tabIndex={0}
        />
      )}

      {/* Code Panel */}
      {isCodeVisible && (
        <section
          className={mergeClasses(styles.panel, styles.codePanel, isCodeMaximized && styles.codePanelMaximized)}
          style={getCodePanelStyle()}
          aria-label="Code editor"
          aria-describedby={CODE_PANEL_DESCRIPTION_ID}
        >
          <CodePanelHeader
            state={codeState === 'maximized' ? 'maximized' : 'normal'}
            onClose={handleCodeClose}
            onToggleMaximize={handleCodeToggleMaximize}
          />
          <div className={styles.codeGroup}>
            <CodeFileToolbar
              activeFile={activeFile}
              fileOptions={fileOptions}
              onFileChange={(value) => onFileChange(value as ActiveFile)}
              fileContent={fileContent}
              parseError={parseError ?? null}
              readOnlyBadge={readOnlyBadge}
            />
            <div className={styles.codeGroupEditor}>
              <FeatureErrorBoundary componentName="CodeEditor">
                <CodeEditor hideHeader readOnly={isEditorReadOnly} />
              </FeatureErrorBoundary>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
